import { IonIcon } from '@ionic/react'
import dayjs from 'dayjs'
import { chevronBackOutline, chevronForwardOutline } from 'ionicons/icons'
import React, { useContext, useMemo } from 'react'
import { ConsumptionDataContext } from '../contexts/ConsumptionDataContext'

// Structural subset shared by every ChartTimeHook variant (consumption, electricity, solar, ...)
interface TimeScrollerTime {
	dayjs: dayjs.Dayjs
	activeTimespan: dayjs.OpUnitType
	timeString: string
	back: () => void
	next: () => void
}

// 'electricity' is currently unhandled below (canGoNext stays false for it), matching prior behavior.
type TimeScrollerMode = 'consumption' | 'price' | 'production' | 'electricity'

interface Props {
	time: TimeScrollerTime
	mode?: TimeScrollerMode
}

const TimeScroller: React.FC<Props> = ({ time, mode = 'consumption' }) => {
	const { lastAvailableConsumptionData } = useContext(ConsumptionDataContext)

	const canGoNext = useMemo(() => {
		const lastAvailable = lastAvailableConsumptionData
		const diffToLatestAvailable = time.dayjs.diff(lastAvailable, time.activeTimespan, true)
		const diffToPresent = time.dayjs.diff(dayjs().startOf('day'), time.activeTimespan, true)
		const currentHour = dayjs().hour()

		const consumptionOk = mode === 'consumption' && diffToLatestAvailable < 0
		const priceOk =
			mode === 'price' && (diffToPresent < 0 || (diffToPresent < 1 && currentHour > 14))
		// Solar production data is reported live (current inverter output), unlike consumption
		// which lags behind a metering pipeline, so it only needs to stay clear of the future.
		const productionOk = mode === 'production' && diffToPresent < 0

		return consumptionOk || priceOk || productionOk
	}, [lastAvailableConsumptionData, time.dayjs, time.activeTimespan, mode])

	return (
		<div id='content-scroller'>
			<span className='text-text-dark dark:text-white'>{time.timeString}</span>

			<div className='chevron-container'>
				<div className='chevron bg-brand-primary-200-light text-brand-secondary-100-light dark:text-brand-secondary-100-dark dark:bg-brand-primary-200-dark button-hover'>
					<IonIcon onClick={time.back} icon={chevronBackOutline} />
				</div>
				{canGoNext ? (
					<div className='chevron bg-brand-primary-200-light text-brand-secondary-100-light dark:text-brand-secondary-100-dark dark:bg-brand-primary-200-dark button-hover'>
						<IonIcon onClick={time.next} icon={chevronForwardOutline} />
					</div>
				) : (
					<div className='chevron disabled bg-brand-primary-200-light text-brand-secondary-100-light dark:text-brand-secondary-200-dark dark:bg-background-100-dark cursor-default'>
						<IonIcon className='disabled' icon={chevronForwardOutline} />
					</div>
				)}
			</div>
		</div>
	)
}
export default TimeScroller
